import { useEffect, useState } from 'react';
import { CheckSquare, MoreHorizontal, Check, X, Eye, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  MetricCard,
} from '@/components/common';
import { approvalsApi, Approval, QueryApprovalParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const statusColors: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  APPROVED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export default function Approvals() {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [decisionDialog, setDecisionDialog] = useState<{
    open: boolean;
    approval?: Approval;
    decision?: 'APPROVED' | 'REJECTED';
  }>({ open: false });
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchApprovals = async (params?: QueryApprovalParams) => {
    try {
      setLoading(true);
      const response = await approvalsApi.getAll(params);
      setApprovals(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params: QueryApprovalParams = {};
    if (statusFilter !== 'all') {
      params.status = statusFilter as Approval['status'];
    }
    fetchApprovals(params);
  }, [statusFilter]);

  const handleDecision = async () => {
    if (!decisionDialog.approval || !decisionDialog.decision) return;

    try {
      setProcessing(true);
      await approvalsApi.decide(
        decisionDialog.approval.id,
        decisionDialog.decision,
        comments || undefined
      );
      toast.success(`Request ${decisionDialog.decision.toLowerCase()}`);
      setDecisionDialog({ open: false });
      setComments('');
      fetchApprovals({ status: statusFilter !== 'all' ? statusFilter as Approval['status'] : undefined });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Calculate metrics
  const pendingCount = approvals.filter(a => a.status === 'PENDING').length;
  const approvedCount = approvals.filter(a => a.status === 'APPROVED').length;
  const rejectedCount = approvals.filter(a => a.status === 'REJECTED').length;

  const columns: Column<Approval>[] = [
    {
      key: 'entityType',
      header: 'Request',
      sortable: true,
      render: (approval) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <CheckSquare className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{approval.entityType}</div>
            <div className="text-sm text-muted-foreground">
              {approval.purchaseOrder?.poNumber || approval.entityId}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (approval) => (
        <div className="text-sm">
          {approval.purchaseOrder && (
            <>
              <div className="font-medium">
                {formatCurrency(approval.purchaseOrder.totalAmount)}
              </div>
              <div className="text-muted-foreground">
                {approval.purchaseOrder.supplier?.name || '-'}
              </div>
            </>
          )}
          {!approval.purchaseOrder && <span className="text-muted-foreground">-</span>}
        </div>
      ),
    },
    {
      key: 'requestedAt',
      header: 'Requested',
      render: (approval) => (
        <div className="text-sm">
          <div className="text-muted-foreground">{formatDate(approval.requestedAt)}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (approval) => (
        <Badge className={statusColors[approval.status]}>
          {approval.status}
        </Badge>
      ),
    },
    {
      key: 'respondedAt',
      header: 'Responded',
      render: (approval) => (
        <div className="text-sm">
          {approval.respondedAt ? (
            <>
              <div className="text-muted-foreground">{formatDate(approval.respondedAt)}</div>
              {approval.approver && (
                <div className="text-muted-foreground">by {approval.approver.fullName}</div>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (approval) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {approval.status === 'PENDING' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDecisionDialog({ open: true, approval, decision: 'APPROVED' })}
                >
                  <Check className="mr-2 h-4 w-4 text-emerald-600" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDecisionDialog({ open: true, approval, decision: 'REJECTED' })}
                >
                  <X className="mr-2 h-4 w-4 text-red-600" />
                  Reject
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Approvals"
        description="Review and process pending approval requests"
        breadcrumbs={[{ label: 'Approvals' }]}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard
          label="Pending"
          value={pendingCount}
          icon={<Clock className="h-5 w-5" />}
          trend={pendingCount > 0 ? { direction: 'neutral', value: '', text: 'awaiting review' } : undefined}
        />
        <MetricCard
          label="Approved"
          value={approvedCount}
          icon={<Check className="h-5 w-5 text-emerald-600" />}
        />
        <MetricCard
          label="Rejected"
          value={rejectedCount}
          icon={<X className="h-5 w-5 text-red-600" />}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={approvals}
        columns={columns}
        loading={loading}
        emptyTitle="No approval requests"
        emptyDescription="All approval requests have been processed."
      />

      {/* Decision Dialog */}
      <Dialog
        open={decisionDialog.open}
        onOpenChange={(open) => {
          setDecisionDialog({ open });
          if (!open) setComments('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionDialog.decision === 'APPROVED' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
            <DialogDescription>
              {decisionDialog.decision === 'APPROVED'
                ? 'Are you sure you want to approve this request?'
                : 'Please provide a reason for rejection.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder={
                decisionDialog.decision === 'APPROVED'
                  ? 'Add optional comments...'
                  : 'Reason for rejection...'
              }
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog({ open: false })}>
              Cancel
            </Button>
            <Button
              variant={decisionDialog.decision === 'APPROVED' ? 'default' : 'destructive'}
              onClick={handleDecision}
              disabled={processing || (decisionDialog.decision === 'REJECTED' && !comments.trim())}
            >
              {processing ? 'Processing...' : decisionDialog.decision === 'APPROVED' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
